/**
 * DuckDB-WASM singleton wrapper.
 *
 * Runs DuckDB in the browser via WebAssembly, giving frontend the SAME SQL
 * engine as the backend Glue Job (also DuckDB). This eliminates dialect
 * drift between preview and execution — what runs in preview will run
 * identically in production.
 *
 * Assets are served from our own S3/CloudFront (bundled via vite-plugin-static-copy
 * from node_modules at build time), NOT jsdelivr CDN. This avoids external CDN
 * dependency and ensures consistent performance / availability.
 *
 * Lazy-init: WASM is only downloaded on first call to runSql(), not on
 * app load. Subsequent calls reuse the same connection.
 */

import * as duckdb from '@duckdb/duckdb-wasm';

let dbPromise: Promise<duckdb.AsyncDuckDB> | null = null;

/**
 * Bundles hosted from our own origin. The duckdb-wasm folder is populated at
 * build time by vite-plugin-static-copy from node_modules/@duckdb/duckdb-wasm/dist/.
 */
const MANUAL_BUNDLES: duckdb.DuckDBBundles = {
  mvp: {
    mainModule: '/duckdb-wasm/duckdb-mvp.wasm',
    mainWorker: '/duckdb-wasm/duckdb-browser-mvp.worker.js',
  },
  eh: {
    mainModule: '/duckdb-wasm/duckdb-eh.wasm',
    mainWorker: '/duckdb-wasm/duckdb-browser-eh.worker.js',
  },
  coi: {
    mainModule: '/duckdb-wasm/duckdb-coi.wasm',
    mainWorker: '/duckdb-wasm/duckdb-browser-coi.worker.js',
    pthreadWorker: '/duckdb-wasm/duckdb-browser-coi.pthread.worker.js',
  },
};

async function initDb(): Promise<duckdb.AsyncDuckDB> {
  const bundle = await duckdb.selectBundle(MANUAL_BUNDLES);

  const worker = new Worker(bundle.mainWorker!);
  const logger = new duckdb.ConsoleLogger(duckdb.LogLevel.WARNING);
  const db = new duckdb.AsyncDuckDB(logger, worker);
  await db.instantiate(bundle.mainModule, bundle.pthreadWorker);
  return db;
}

function getDb(): Promise<duckdb.AsyncDuckDB> {
  if (!dbPromise) dbPromise = initDb();
  return dbPromise;
}

/**
 * Execute SQL against an array of row objects.
 * The array is registered as both `source` and `source_table` (aliases) so
 * users can reference either name — matching the Glue Job backend behavior.
 */
export async function runSql<T = Record<string, unknown>>(
  sql: string,
  rows: Record<string, unknown>[]
): Promise<T[]> {
  const db = await getDb();
  const conn = await db.connect();
  try {
    // Drop any previous `source` table/view to allow re-running preview
    await conn.query('DROP TABLE IF EXISTS source');
    await conn.query('DROP VIEW IF EXISTS source_table');

    const json = JSON.stringify(rows);
    await db.registerFileText('source.json', json);
    await conn.insertJSONFromPath('source.json', { name: 'source' });
    // Create alias view so both names work
    await conn.query('CREATE OR REPLACE VIEW source_table AS SELECT * FROM source');

    const result = await conn.query(sql);
    // Convert Arrow result to plain JS objects
    return result.toArray().map((row: any) => row.toJSON()) as T[];
  } finally {
    await conn.close();
  }
}

/**
 * Validate SQL syntax AND column references against real source schema.
 * If rows provided: creates real `source` table from data, runs EXPLAIN.
 *   This catches both syntax errors AND "column not found" errors.
 * If rows omitted: only syntax-level check with empty placeholder (may miss column errors).
 */
export async function validateSql(
  sql: string,
  rows?: Record<string, unknown>[]
): Promise<{ valid: boolean; error?: string }> {
  const db = await getDb();
  const conn = await db.connect();
  try {
    await conn.query('DROP TABLE IF EXISTS source');
    await conn.query('DROP VIEW IF EXISTS source_table');

    if (rows && rows.length > 0) {
      // Real schema from actual data
      const json = JSON.stringify(rows);
      await db.registerFileText('source.json', json);
      await conn.insertJSONFromPath('source.json', { name: 'source' });
    } else {
      // Fallback: empty placeholder (only catches pure syntax errors)
      await conn.query('CREATE OR REPLACE TABLE source AS SELECT 1 AS dummy WHERE 0');
    }
    await conn.query('CREATE OR REPLACE VIEW source_table AS SELECT * FROM source');
    await conn.query(`EXPLAIN ${sql}`);
    return { valid: true };
  } catch (e: any) {
    return { valid: false, error: e.message };
  } finally {
    await conn.close();
  }
}
